import React, { useRef, useState } from "react";
import { Paperclip, X, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import AttachmentPreview from "./AttachmentPreview";

const MAX_FILES = 5;
const MAX_UPLOAD = 25 * 1024 * 1024;

/** Multi-file uploader: keeps a working list of attachment records that have
 *  been uploaded to /api/upload but not yet attached to a post. Renders the
 *  paperclip-picker + a chip strip of pending files. Emits `onChange(ids[])`. */
export default function MultiUploader({ ids, onChange, max = MAX_FILES, disabled = false }) {
  const [busy, setBusy] = useState(false);
  const [pending, setPending] = useState([]);           // hydrated file records
  const fileRef = useRef(null);

  React.useEffect(() => {
    // Reset if the parent clears the ids array (e.g. after submit).
    if ((ids?.length || 0) === 0 && pending.length > 0) setPending([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ids]);

  const onPick = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    if (pending.length + files.length > max) {
      toast.error(`MAX ${max} FILES PER POST`);
      return;
    }
    setBusy(true);
    const added = [];
    for (const f of files) {
      if (f.size > MAX_UPLOAD) {
        toast.error(`${f.name} EXCEEDS 25 MB`);
        continue;
      }
      try {
        const fd = new FormData();
        fd.append("file", f);
        const r = await api.post("/upload", fd, {
          headers: { "Content-Type": "multipart/form-data" }, timeout: 120000,
        });
        added.push(r.data);
      } catch (err) {
        toast.error(err?.response?.data?.detail || `UPLOAD FAILED: ${f.name}`);
      }
    }
    if (added.length) {
      const next = [...pending, ...added];
      setPending(next);
      onChange(next.map(x => x.id));
    }
    setBusy(false);
  };

  const drop = (idx) => {
    const next = pending.filter((_, i) => i !== idx);
    setPending(next);
    onChange(next.map(x => x.id));
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <input
          ref={fileRef} type="file" multiple className="hidden"
          data-testid="mu-file-input" onChange={onPick} disabled={disabled}
        />
        <button
          type="button" onClick={() => fileRef.current?.click()}
          data-testid="mu-attach"
          disabled={disabled || busy || pending.length >= max}
          className="sat-btn !py-2 !px-3 disabled:opacity-40"
        >
          {busy ? <Loader2 size={11} className="animate-spin" /> : <Paperclip size={11} />}
          {busy ? "UPLOADING…" : "ATTACH FILES"}
        </button>
        <span className="sat-label text-[color:var(--sat-text-dim)]">
          {pending.length}/{max} · UP TO 25 MB EACH
        </span>
      </div>
      {pending.length > 0 && (
        <div className="flex flex-wrap gap-2" data-testid="mu-pending">
          {pending.map((f, i) => (
            <div key={f.id} className="relative group" data-testid={`mu-pending-${i}`}>
              <AttachmentPreview attachment={f} compact />
              <button
                type="button" data-sfx-silent
                onClick={() => drop(i)}
                aria-label="Remove attachment"
                data-testid={`mu-remove-${i}`}
                className="absolute -top-2 -right-2 border border-[color:var(--sat-cyan)] bg-black p-0.5"
              >
                <X size={10} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
